import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Joi from 'joi';

import { useUpdateUserMutation } from 'api/main';
import { getRenderRoleName, validateSchema } from 'utils/helpers';
import { EMAIL_VALIDATION_OPTIONS, NAME_REGEX } from 'utils/constants';
import { useUserAvailableRoles } from '../hooks';
import { texts } from 'utils/languages/fr';

import {
    ActionButton,
    CancelButton,
    CustomDialogTitle,
    CustomSelect,
    CustomPhoneField,
    CustomTextField,
} from 'components';
import { Dialog, DialogActions, DialogContent, MenuItem } from '@mui/material';

const renderRole = role => (
    <MenuItem key={role.id} value={role.id}>
        {getRenderRoleName(role)}
    </MenuItem>
);

const schema = {
    firstName: Joi.string().required().trim().min(2).max(120).pattern(NAME_REGEX).messages({
        'string.empty': texts.VEUILLEZ_SAISIR_VOTRE_PRENOM,
        'string.min': texts.FIRST_NAME_LENGTH,
        'string.max': texts.FIRST_NAME_LENGTH,
        'string.pattern.base': texts.FIRST_NAME_PATTERN,
    }),
    lastName: Joi.string().required().trim().min(2).max(120).pattern(NAME_REGEX).messages({
        'string.empty': texts.VEUILLEZ_SAISIR_VOTRE_NOM,
        'string.min': texts.LAST_NAME_LENGTH,
        'string.max': texts.LAST_NAME_LENGTH,
        'string.pattern.base': texts.LAST_NAME_PATTERN,
    }),
    phoneNumber: Joi.string().required().invalid('+').messages({
        'string.empty': texts.SAISIR_LE_CHAMP,
        'any.invalid': texts.SAISIR_LE_CHAMP,
    }),
    email: Joi.string().required().email(EMAIL_VALIDATION_OPTIONS).messages({
        'string.empty': texts.SAISIR_VOTRE_ADRESSE_EMAIL,
        'string.email': texts.ADRESSE_EMAIL_SAISIE_PAS_VALIDE,
    }),
};

const EditUserDialog = ({ onClose, user, open }) => {
    const [updateUser, { isLoading, isSuccess }] = useUpdateUserMutation();
    const userSubscription = user.subscriptions[0];
    const availableRoles = useUserAvailableRoles(userSubscription);

    const [form, setForm] = useState({
        role: user.subscriptions[0].role.id,
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        phoneNumber: user.phoneNumber,
    });

    const [errors, setErrors] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
    });

    const handleChange = useCallback(
        key => e => {
            setForm(prevState => ({ ...prevState, [key]: e.target.value }));
        },
        []
    );
    const handlePhoneChange = useCallback(newValue => {
        setForm(prevState => ({ ...prevState, phoneNumber: newValue }));
    }, []);

    const handleConfirm = useCallback(() => {
        const { valid, newErrors } = validateSchema(schema, form);
        if (valid) {
            setErrors('');
            const payload = {
                id: user.id,
                firstName: form.firstName,
                lastName: form.lastName,
                email: form.email,
                phoneNumber: form.phoneNumber,
                subscriptions: [{ id: userSubscription.id, role: `api/roles/${form.role}` }],
            };
            updateUser(payload);
        } else {
            setErrors(newErrors);
        }
    }, [form, user.id, userSubscription.id, updateUser]);

    useEffect(() => {
        if (isSuccess) {
            onClose();
        }
    }, [isSuccess, onClose]);

    useEffect(() => {
        if (open) {
            setForm({
                role: user.subscriptions[0].role.id,
                firstName: user.firstName,
                lastName: user.lastName,
                email: user.email,
                phoneNumber: user.phoneNumber,
            });
        }
    }, [open, user]);

    return (
        <Dialog open={open} onKeyDown={e => e.stopPropagation()}>
            <CustomDialogTitle title={texts.EDIT_USER} handleClose={isLoading ? null : onClose} />
            <DialogContent>
                <CustomTextField
                    fullWidth
                    label={texts.PRENOM}
                    value={form.firstName}
                    onChange={handleChange('firstName')}
                    error={errors.firstName}
                />
                <CustomTextField
                    fullWidth
                    label={texts.NOM}
                    value={form.lastName}
                    onChange={handleChange('lastName')}
                    error={errors.lastName}
                />
                <CustomTextField
                    fullWidth
                    inputProps={{
                        autoCapitalize: 'off',
                        autoComplete: 'off',
                        spellCheck: 'off',
                        autoCorrect: 'off',
                    }}
                    label={texts.E_MAIL}
                    value={form.email}
                    onChange={handleChange('email')}
                    error={errors.email}
                />
                <CustomPhoneField
                    fullWidth
                    label={texts.TELEPHONE}
                    value={form.phoneNumber}
                    onChange={handlePhoneChange}
                    error={errors.phoneNumber}
                    hideInfoButton
                />
                <CustomSelect
                    label={texts.SELECTIONNEZ_LE_ROLE}
                    value={form.role}
                    onChange={handleChange('role')}
                    error={errors.role}
                    helperText={errors.role}
                >
                    {availableRoles.map(renderRole)}
                </CustomSelect>
            </DialogContent>
            <DialogActions>
                <CancelButton disabled={isLoading} onClick={onClose}>
                    {texts.CANCEL}
                </CancelButton>
                <ActionButton
                    size="small"
                    sx={{ padding: '3px 5px' }}
                    loading={isLoading}
                    disabled={isLoading}
                    onClick={handleConfirm}
                >
                    {texts.CONFIRMER}
                </ActionButton>
            </DialogActions>
        </Dialog>
    );
};

EditUserDialog.propTypes = {
    onClose: PropTypes.func.isRequired,
    open: PropTypes.bool,
    user: PropTypes.shape({
        id: PropTypes.number.isRequired,
        firstName: PropTypes.string.isRequired,
        lastName: PropTypes.string.isRequired,
        email: PropTypes.string.isRequired,
        phoneNumber: PropTypes.string.isRequired,
        subscriptions: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.number.isRequired,
                role: PropTypes.shape({
                    id: PropTypes.number.isRequired,
                    name: PropTypes.string.isRequired,
                    category: PropTypes.object.isRequired,
                }),
            })
        ),
    }),
};

export default EditUserDialog;
